import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api';
import { useAuth } from '../auth/AuthProvider';
import {
  fetchActiveUsers,
  fetchDailyActiveUsers,
  fetchDashboardKpis,
  fetchPerMapStats,
  fetchPlaytimeSeries,
  fetchProgressDistribution,
  fetchRecentEvents,
  fetchRetentionCohort,
  type DashboardActiveUser,
  type DashboardDauRow,
  type DashboardKpis,
  type DashboardPerMapRow,
  type DashboardPlaytimeBucket,
  type DashboardPlaytimeSeries,
  type DashboardProgressBucket,
  type DashboardRecentEvent,
  type DashboardRetentionRow,
  type PlaytimeBucketUnit,
  type RecentEventsOpts,
  type WindowOpts,
} from '../apiClient/analytics';
import {
  progressTierLabel,
  type AnalyticsEventType,
  type ProgressTier,
} from '@shared/analytics';
import { GAME_MODES, modeLabel, type GameMode } from '@shared/chart';
import { variantLabel } from '@shared/gameTypes';
import './analyticsDashboard.css';

/** Event types we consider "gameplay" for the default recent-events filter. */
const SONG_EVENT_TYPES: ReadonlyArray<AnalyticsEventType> = [
  'song_start',
  'song_end',
  'song_abandon',
];

type RangeKey = '24h' | '7d' | '30d' | 'all';

const RANGE_LABELS: Record<RangeKey, string> = {
  '24h': 'Last 24h',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  all: 'All time',
};

function windowFor(range: RangeKey): WindowOpts {
  if (range === 'all') {
    return {};
  }
  const now = new Date();
  const since = new Date(now);
  if (range === '24h') {
    since.setUTCHours(since.getUTCHours() - 24);
  } else if (range === '7d') {
    since.setUTCDate(since.getUTCDate() - 7);
  } else {
    since.setUTCDate(since.getUTCDate() - 30);
  }
  return { since };
}

/**
 * DAU and retention queries take a discrete `days` integer rather than
 * a (since, until) window — that's the natural shape for cohort
 * tables. Map the user-facing range tab onto the closest day count so
 * those sections stay in sync with the rest of the dashboard.
 */
function daysFor(range: RangeKey): number {
  switch (range) {
    case '24h':
      return 1;
    case '7d':
      return 7;
    case '30d':
      return 30;
    case 'all':
      return 90;
  }
}

/**
 * Stable per-mode accent colour (design-token CSS var) for the playtime
 * charts. One distinct colour per mode so stacked segments and the
 * single-mode chart stay legible; mirrors the mode-picker palette,
 * giving Pulse violet (the picker reuses cyan for both Pulse and Waves,
 * which would collide when stacked).
 */
const MODE_ACCENT_VARS: Record<GameMode, string> = {
  pulse: 'var(--accent-violet)',
  circles: 'var(--accent-magenta)',
  piano: 'var(--accent-lime)',
  guitar: 'var(--accent-amber)',
  waves: 'var(--accent-cyan)',
  jump: 'var(--accent-green)',
};

/**
 * Playtime charts need an explicit bounded window plus a bucket
 * granularity to anchor the server-side time spine. The 24h tab buckets
 * by hour; everything wider buckets by day. "All time" mirrors
 * {@link daysFor} and caps at 90 days so the axis stays readable.
 */
function playtimeRangeFor(range: RangeKey): { since: Date; until: Date; bucket: PlaytimeBucketUnit } {
  const until = new Date();
  const since = new Date(until);
  if (range === '24h') {
    since.setUTCHours(since.getUTCHours() - 24);
    return { since, until, bucket: 'hour' };
  }
  since.setUTCDate(since.getUTCDate() - daysFor(range));
  return { since, until, bucket: 'day' };
}

interface DashboardData {
  kpis: DashboardKpis;
  distribution: DashboardProgressBucket[];
  perMap: DashboardPerMapRow[];
  playtime: DashboardPlaytimeSeries;
  dau: DashboardDauRow[];
  retention: DashboardRetentionRow[];
  /** Wall-clock at which this snapshot was loaded. Surfaced in the header so the operator knows how stale the dashboard is. */
  loadedAt: number;
}

export function AnalyticsDashboardPage() {
  const { user } = useAuth();
  const [range, setRange] = useState<RangeKey>('7d');
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  /** Toggle for the global recent-events feed: only song lifecycle events (default) vs every event type. */
  const [recentSongOnly, setRecentSongOnly] = useState(true);
  const [recent, setRecent] = useState<DashboardRecentEvent[] | null>(null);
  const [recentLoading, setRecentLoading] = useState(true);
  /** Bumped manually to retrigger the data-load effects without changing the range. */
  const [refreshTick, setRefreshTick] = useState(0);

  // Heavy aggregation queries — fired only on range change or manual
  // Refresh, NOT on the recent-events filter toggle. Keeping this
  // narrow matters: each call hits the events table with GROUP BY,
  // percentile_cont, etc., and we don't want a UI checkbox to trigger
  // six DB scans.
  useEffect(() => {
    const window = windowFor(range);
    let cancelled = false;
    setLoading(true);
    setError(null);
    setForbidden(false);
    const days = daysFor(range);
    Promise.all([
      fetchDashboardKpis(window),
      fetchProgressDistribution(window),
      fetchPerMapStats(window),
      fetchPlaytimeSeries(playtimeRangeFor(range)),
      fetchDailyActiveUsers(days),
      fetchRetentionCohort(days),
    ])
      .then(([kpis, distribution, perMap, playtime, dau, retention]) => {
        if (cancelled) {
          return;
        }
        setData({
          kpis,
          distribution: distribution.buckets,
          perMap: perMap.rows,
          playtime,
          dau: dau.series,
          retention: retention.cohorts,
          loadedAt: Date.now(),
        });
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setForbidden(true);
        } else {
          const msg = err instanceof Error ? err.message : String(err);
          setError(`Failed to load analytics: ${msg}`);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [range, refreshTick]);

  // Recent-events feed lives on its own clock so toggling the
  // "Songs only" filter is cheap (one query, one indexed scan)
  // instead of refetching every aggregation above.
  useEffect(() => {
    let cancelled = false;
    setRecentLoading(true);
    const recentOpts: RecentEventsOpts = recentSongOnly
      ? { limit: 50, eventTypes: SONG_EVENT_TYPES }
      : { limit: 50 };
    fetchRecentEvents(recentOpts)
      .then((res) => {
        if (cancelled) {
          return;
        }
        setRecent(res.events);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setForbidden(true);
        } else {
          const msg = err instanceof Error ? err.message : String(err);
          setError(`Failed to load recent events: ${msg}`);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setRecentLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [recentSongOnly, refreshTick]);

  const handleRefresh = useCallback(() => {
    setRefreshTick((n) => n + 1);
  }, []);

  return (
    <div className="analytics-page">
      <header className="analytics-header">
        <div>
          <h1>Analytics</h1>
          <p className="muted">
            Product telemetry from real plays. Driven by the
            <code> analytics_events </code>
            table in Postgres.
          </p>
        </div>
        <div className="analytics-header-controls">
          <button
            type="button"
            className="btn analytics-refresh-btn"
            onClick={handleRefresh}
            disabled={loading || recentLoading}
            data-testid="analytics-refresh"
            title="Re-fetch every chart from the server"
          >
            {loading || recentLoading ? 'Refreshing…' : 'Refresh'}
          </button>
          <nav className="analytics-range-tabs" data-testid="range-tabs">
            {(Object.keys(RANGE_LABELS) as RangeKey[]).map((r) => (
              <button
                key={r}
                type="button"
                className={r === range ? 'active' : ''}
                onClick={() => setRange(r)}
              >
                {RANGE_LABELS[r]}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {forbidden && <ForbiddenPanel uid={user?.uid ?? null} />}

      {error && !forbidden && (
        <div className="error-box analytics-error" data-testid="analytics-error">
          {error}
          <Link to="/" className="btn">Home</Link>
        </div>
      )}

      {loading && !data && !error && !forbidden && (
        <div className="loading-screen">
          <p className="muted">Crunching the data...</p>
        </div>
      )}

      {data && (
        <>
          {user && (
            <div className="analytics-whoami" data-testid="analytics-whoami">
              <span className="muted">Signed in as</span>
              <code>{user.uid}</code>
              <CopyButton value={user.uid} />
              <span className="muted faint analytics-loaded-at">
                Loaded {formatLocalDateTime(new Date(data.loadedAt).toISOString())}
              </span>
            </div>
          )}
          <LifecycleExplainerSection />
          <KpiCards data={data.kpis} />
          <ModeBreakdownSection data={data.kpis} />
          <PlaytimeSection data={data.playtime} />
          <PlaytimeByModeSection data={data.playtime} />
          <ProgressDistributionSection buckets={data.distribution} />
          <PerMapCardsSection rows={data.perMap} />
          <DauSection rows={data.dau} />
          <RetentionSection rows={data.retention} />
          <RecentEventsSection
            events={recent}
            loading={recentLoading}
            songOnly={recentSongOnly}
            onToggleSongOnly={setRecentSongOnly}
          />
          <PlayerDrilldownSection refreshTick={refreshTick} />
        </>
      )}
    </div>
  );
}

/**
 * Cheat-sheet for what the song-lifecycle event names mean. Sits at the
 * top of the dashboard so the KPIs, histograms, and per-map sections
 * below all have a shared vocabulary the reader can refer back to.
 */
function LifecycleExplainerSection() {
  return (
    <section className="analytics-section analytics-explainer" data-testid="lifecycle-explainer">
      <h2>Song lifecycle</h2>
      <ul className="analytics-explainer-list">
        <li>
          <span className="analytics-event-badge analytics-event-start">start</span>
          <span>Player kicks off a song — audio engine has started playing.</span>
        </li>
        <li>
          <span className="analytics-event-badge analytics-event-end">end</span>
          <span>
            Engine resolved the run — the player <strong>cleared</strong> the song or
            <strong> ran out of HP</strong>. Either way the engine reached an outcome (vs
            an <code>abandon</code>, where the player left before it could).
          </span>
        </li>
        <li>
          <span className="analytics-event-badge analytics-event-abandon">abandon</span>
          <span>
            Player bailed mid-run — closed the tab, hit back, or navigated away before the
            engine finished.
          </span>
        </li>
      </ul>
      <p className="muted faint analytics-explainer-foot">
        Reach % = <code>ends / starts</code> (how sticky is the song?) · Clear % ={' '}
        <code>completions / ends</code> (how often do engaged players actually beat it?)
      </p>
    </section>
  );
}

/**
 * Shown when the dashboard returns 403. Surfaces the signed-in user's
 * uid so the operator can copy-paste it into the `ANALYTICS_ADMIN_UIDS`
 * Fly secret — no rummaging through Firebase console required.
 */
function ForbiddenPanel({ uid }: { uid: string | null }) {
  const flyCommand = uid
    ? `fly secrets set ANALYTICS_ADMIN_UIDS="${uid}" -a gravity-dash`
    : 'fly secrets set ANALYTICS_ADMIN_UIDS="<your-uid>" -a gravity-dash';
  return (
    <div className="analytics-forbidden" data-testid="analytics-forbidden">
      <h2>You're not on the admin list yet</h2>
      <p className="muted">
        The dashboard is gated on the <code>ANALYTICS_ADMIN_UIDS</code> Fly
        secret. Add your Firebase uid to grant yourself access.
      </p>
      {uid ? (
        <>
          <div className="analytics-forbidden-row">
            <span className="muted">Your uid</span>
            <code data-testid="forbidden-uid">{uid}</code>
            <CopyButton value={uid} />
          </div>
          <p className="muted">Then run, locally:</p>
          <div className="analytics-forbidden-row">
            <code className="analytics-forbidden-cmd">{flyCommand}</code>
            <CopyButton value={flyCommand} />
          </div>
          <p className="muted faint">
            Fly applies the new secret on the next deploy. Trigger one by
            pushing a commit or running <code>fly deploy</code>. To add
            multiple admins, separate uids with commas.
          </p>
        </>
      ) : (
        <p className="muted">
          Sign in first, then refresh this page — your uid will appear here.
        </p>
      )}
      <Link to="/" className="btn">Home</Link>
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-small"
      onClick={() => {
        void navigator.clipboard
          .writeText(value)
          .then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          })
          .catch((err: unknown) => {
            console.error('Clipboard write failed:', err);
          });
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function KpiCards({ data }: { data: DashboardKpis }) {
  const o = data.overall;
  const completionPct = (o.completionRate * 100).toFixed(1);
  const reachPct = (o.startCompletionRate * 100).toFixed(1);
  const abandonPct =
    o.songStarts === 0 ? undefined : `${((o.songAbandons / o.songStarts) * 100).toFixed(1)}% of starts`;
  return (
    <section className="analytics-section">
      <h2>Overall</h2>
      <div className="analytics-kpis" data-testid="analytics-kpis">
        <Kpi label="Song starts" value={o.songStarts.toLocaleString()} sub="audio engine kicked in" />
        <Kpi
          label="Song ends"
          value={o.songEnds.toLocaleString()}
          sub={`${reachPct}% reach (ended / started)`}
        />
        <Kpi
          label="Completions"
          value={o.songCompletions.toLocaleString()}
          sub={`${completionPct}% clear (cleared / ended)`}
        />
        <Kpi
          label="Abandons"
          value={o.songAbandons.toLocaleString()}
          sub={abandonPct ? `${abandonPct} (left mid-run)` : 'left mid-run'}
        />
        <Kpi label="Distinct players" value={o.distinctPlayers.toLocaleString()} />
        <Kpi label="Sessions" value={o.sessions.toLocaleString()} />
      </div>
    </section>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="analytics-kpi">
      <div className="analytics-kpi-value">{value}</div>
      <div className="analytics-kpi-label">{label}</div>
      {sub && <div className="analytics-kpi-sub">{sub}</div>}
    </div>
  );
}

function ModeBreakdownSection({ data }: { data: DashboardKpis }) {
  if (data.byMode.length === 0) {
    return null;
  }
  return (
    <section className="analytics-section">
      <h2>By mode</h2>
      <table className="analytics-table" data-testid="mode-table">
        <thead>
          <tr>
            <th>Mode</th>
            <th>Starts</th>
            <th>Ends</th>
            <th title="Total time played across ended + abandoned runs (sum of run durations)">
              Playtime
            </th>
            <th title="Of starts, fraction that reached song_end (engine finished, not abandoned)">
              Reach %
            </th>
            <th>Completions</th>
            <th title="Of ends, fraction where the player actually cleared">
              Clear %
            </th>
            <th>Avg progress tier</th>
            <th>Distinct players</th>
          </tr>
        </thead>
        <tbody>
          {data.byMode.map((m) => (
            <tr key={m.mode}>
              <td>{m.mode}</td>
              <td>{m.songStarts.toLocaleString()}</td>
              <td>{m.songEnds.toLocaleString()}</td>
              <td>{m.totalDurationMs !== null ? formatDuration(m.totalDurationMs) : '—'}</td>
              <td>{(m.startCompletionRate * 100).toFixed(1)}%</td>
              <td>{m.songCompletions.toLocaleString()}</td>
              <td>{(m.completionRate * 100).toFixed(1)}%</td>
              <td>{m.avgProgressTier !== null ? m.avgProgressTier.toFixed(2) : '—'}</td>
              <td>{m.distinctPlayers.toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ProgressDistributionSection({ buckets }: { buckets: DashboardProgressBucket[] }) {
  const total = useMemo(() => buckets.reduce((sum, b) => sum + b.count, 0), [buckets]);
  if (total === 0) {
    return null;
  }
  // Fill missing tiers so the histogram has a stable 0..6 layout.
  const full: DashboardProgressBucket[] = [];
  for (let i = 0; i <= 6; i += 1) {
    const found = buckets.find((b) => b.tier === i);
    full.push({ tier: i, count: found?.count ?? 0 });
  }
  return (
    <section className="analytics-section">
      <h2>How far do players get? (overall)</h2>
      <p className="muted">
        Where every run landed when it stopped — natural <strong>end</strong> (cleared
        or out of HP) <em>or</em> <strong>abandon</strong> (player left mid-run). Higher
        tier = further through the song. {total.toLocaleString()} run{total === 1 ? '' : 's'}
        {' '}in this window.
      </p>
      <TierHistogram buckets={full} total={total} />
    </section>
  );
}

/**
 * Shared bar list used for both the overall distribution and the
 * per-map mini-histograms. Renders one row per tier 0..6 with a
 * gradient fill width proportional to the max bucket.
 */
function TierHistogram({
  buckets,
  total,
  compact = false,
}: {
  buckets: DashboardProgressBucket[];
  total: number;
  compact?: boolean;
}) {
  const max = Math.max(...buckets.map((b) => b.count), 1);
  return (
    <div
      className={`analytics-histogram${compact ? ' analytics-histogram-compact' : ''}`}
      data-testid={compact ? undefined : 'progress-histogram'}
    >
      {buckets.map((b) => {
        const pct = total === 0 ? 0 : (b.count / total) * 100;
        const width = (b.count / max) * 100;
        return (
          <div className="analytics-histogram-row" key={b.tier}>
            <div className="analytics-histogram-label">
              {progressTierLabel(b.tier as ProgressTier)}
            </div>
            <div className="analytics-histogram-bar">
              <div className="analytics-histogram-fill" style={{ width: `${width}%` }} />
            </div>
            <div className="analytics-histogram-count">
              {b.count.toLocaleString()}
              {total > 0 && (
                <span className="analytics-histogram-pct"> ({pct.toFixed(0)}%)</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Per-map breakdown rendered as cards. Each card answers "how far do
 * players get on THIS song variant?" with a mini-histogram, plus a
 * stat strip with starts / reach / clear / avg run / total playtime.
 * Replaces the dense 12-column table that buried p10/p50/p90 behind raw
 * numbers — same data, way more readable.
 *
 * Cards are sorted by song_starts descending (per the SQL) so the
 * most-played songs surface first.
 */
function PerMapCardsSection({ rows }: { rows: DashboardPerMapRow[] }) {
  if (rows.length === 0) {
    return null;
  }
  return (
    <section className="analytics-section">
      <h2>How far do players get? (per song)</h2>
      <p className="muted">
        One card per (mode, song, variant, difficulty). Same tier distribution as the
        overall chart above, just sliced by song.
      </p>
      <div className="analytics-map-grid" data-testid="per-map-cards">
        {rows.map((r) => (
          <PerMapCard key={`${r.mode}-${r.mapSlug}-${r.variant}-${r.difficulty}`} row={r} />
        ))}
      </div>
    </section>
  );
}

function PerMapCard({ row }: { row: DashboardPerMapRow }) {
  const total = row.tierCounts.reduce((sum, c) => sum + c, 0);
  const buckets: DashboardProgressBucket[] = row.tierCounts.map((count, tier) => ({
    tier,
    count,
  }));
  // Reach / clear can't be meaningfully expressed without their
  // denominator — render an em-dash for those cases so the card
  // doesn't lie. (Reach undefined when no one started; clear
  // undefined when no run got to song_end.)
  const reach =
    row.songStarts === 0
      ? '—'
      : `${((row.songEnds / row.songStarts) * 100).toFixed(0)}%`;
  const clear = row.songEnds === 0 ? '—' : `${(row.completionRate * 100).toFixed(0)}%`;
  return (
    <article className="analytics-map-card">
      <header className="analytics-map-card-header">
        <div className="analytics-map-card-title">
          <span className="analytics-map-card-mode">{row.mode}</span>
          <span className="analytics-map-card-song">{row.title ?? row.mapSlug}</span>
        </div>
        <div className="analytics-map-card-tags">
          <span className="analytics-map-card-tag">{variantLabel(row.variant)}</span>
          <span className="analytics-map-card-tag">Diff {row.difficulty}</span>
        </div>
      </header>
      <div className="analytics-map-card-stats">
        <MapStat label="Starts" value={row.songStarts.toLocaleString()} />
        <MapStat label="Reach" value={reach} hint={`${row.songEnds} ended`} />
        <MapStat
          label="Clear"
          value={clear}
          hint={`${row.songCompletions} cleared`}
        />
        <MapStat
          label="Avg run"
          value={row.avgDurationMs !== null ? formatDuration(row.avgDurationMs) : '—'}
        />
        <MapStat
          label="Playtime"
          value={row.totalDurationMs !== null ? formatDuration(row.totalDurationMs) : '—'}
        />
      </div>
      {total === 0 ? (
        <p className="muted faint analytics-map-card-empty">
          No ends or abandons with tier info yet — only starts so far.
        </p>
      ) : (
        <TierHistogram buckets={buckets} total={total} compact />
      )}
    </article>
  );
}

function MapStat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="analytics-map-stat">
      <div className="analytics-map-stat-value">{value}</div>
      <div className="analytics-map-stat-label">{label}</div>
      {hint && <div className="analytics-map-stat-hint">{hint}</div>}
    </div>
  );
}

/** One vertical bar in a playtime chart: a value-labelled stack of coloured segments. */
interface PlaytimeBarModel {
  /** React key + bucket identity. */
  key: string;
  /** Short x-axis label (e.g. `14:00` or `Jun 6`). */
  label: string;
  /** Native-tooltip text shown on hover over the whole column. */
  title: string;
  /** Bar height value (ms) — drives the stack height relative to `maxMs`. */
  totalMs: number;
  /** Stacked segments bottom-to-top; `flex` is proportional to `ms`. */
  segments: ReadonlyArray<{ key: string; ms: number; colorVar: string; title: string }>;
}

/**
 * Shared vertical-bar renderer for both playtime charts. Reuses the DAU
 * bar styling (`analytics-bar-*`) for visual consistency, wrapped in a
 * horizontal scroller so a dense hourly or multi-month daily axis stays
 * usable on narrow screens. `maxMs` is the tallest bar in the set, so
 * heights are comparable across the chart.
 */
function PlaytimeBarChart({
  bars,
  maxMs,
  testId,
}: {
  bars: ReadonlyArray<PlaytimeBarModel>;
  maxMs: number;
  testId: string;
}) {
  return (
    <div className="analytics-chart-scroll">
      <div className="analytics-bars" data-testid={testId}>
        {bars.map((bar) => {
          const heightPct = maxMs === 0 ? 0 : (bar.totalMs / maxMs) * 100;
          return (
            <div className="analytics-bar-col" key={bar.key} title={bar.title}>
              <div className="analytics-bar-value">
                {bar.totalMs > 0 ? formatDurationShort(bar.totalMs) : ''}
              </div>
              <div className="analytics-bar-track">
                <div className="analytics-bar-stack" style={{ height: `${heightPct}%` }}>
                  {bar.segments.map((seg) => (
                    <div
                      key={seg.key}
                      className="analytics-playtime-seg"
                      style={{ flex: seg.ms, background: seg.colorVar }}
                      title={seg.title}
                    />
                  ))}
                </div>
              </div>
              <div className="analytics-bar-date">{bar.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Colour key mapping each mode's swatch to its label, shared by the stacked chart. */
function ModeLegend() {
  return (
    <div className="analytics-dau-legend analytics-mode-legend">
      {GAME_MODES.map((mode) => (
        <span className="analytics-mode-legend-item" key={mode}>
          <span className="analytics-dau-swatch" style={{ background: MODE_ACCENT_VARS[mode] }} />
          <span>{modeLabel(mode)}</span>
        </span>
      ))}
    </div>
  );
}

/**
 * Total playtime per bucket, stacked by mode. Answers "how much are
 * people playing, and which modes drive it?" over the selected period —
 * hourly bars for the 24h tab, daily bars otherwise.
 */
function PlaytimeSection({ data }: { data: DashboardPlaytimeSeries }) {
  const { series, bucket } = data;
  const totalMs = series.reduce((sum, b) => sum + b.totalMs, 0);
  const maxMs = series.reduce((m, b) => Math.max(m, b.totalMs), 0);
  const bars: PlaytimeBarModel[] = series.map((b) => ({
    key: b.bucket,
    label: formatBucketLabel(b.bucket, bucket),
    title: playtimeTooltip(b, bucket),
    totalMs: b.totalMs,
    segments: GAME_MODES.filter((mode) => (b.byMode[mode] ?? 0) > 0).map((mode) => ({
      key: mode,
      ms: b.byMode[mode] as number,
      colorVar: MODE_ACCENT_VARS[mode],
      title: `${modeLabel(mode)}: ${formatDuration(b.byMode[mode] as number)}`,
    })),
  }));
  return (
    <section className="analytics-section" data-testid="playtime-total">
      <h2>Playtime over time</h2>
      <p className="muted">
        Total time played per {bucket}, stacked by mode. Sums every run&apos;s duration
        (ends + abandons). Bucketed in UTC.
      </p>
      <div className="analytics-dau-summary">
        <div className="analytics-dau-summary-item">
          <span className="analytics-dau-summary-value" data-testid="playtime-total-value">
            {formatDuration(totalMs)}
          </span>
          <span className="analytics-dau-summary-label">Total playtime</span>
        </div>
        <ModeLegend />
      </div>
      {totalMs === 0 ? (
        <p className="muted faint">No playtime recorded in this window.</p>
      ) : (
        <PlaytimeBarChart bars={bars} maxMs={maxMs} testId="playtime-bars" />
      )}
    </section>
  );
}

/**
 * Playtime per bucket for a single, operator-selected mode. Defaults to
 * the busiest mode in the window so the chart is populated on first
 * paint; the tab row lets the operator pivot to any mode (including ones
 * with no plays, which render the empty state).
 */
function PlaytimeByModeSection({ data }: { data: DashboardPlaytimeSeries }) {
  const { series, bucket } = data;
  const totalsByMode = useMemo(() => {
    const totals = Object.fromEntries(GAME_MODES.map((m) => [m, 0])) as Record<GameMode, number>;
    for (const b of series) {
      for (const mode of GAME_MODES) {
        totals[mode] += b.byMode[mode] ?? 0;
      }
    }
    return totals;
  }, [series]);
  // Lazy initial selection = busiest mode at mount. Persisted across
  // range changes so the operator's pick isn't reset when they retune
  // the period.
  const [selectedMode, setSelectedMode] = useState<GameMode>(() =>
    GAME_MODES.reduce((best, m) => (totalsByMode[m] > totalsByMode[best] ? m : best), GAME_MODES[0]),
  );
  const colorVar = MODE_ACCENT_VARS[selectedMode];
  const selectedTotal = totalsByMode[selectedMode];
  const maxMs = series.reduce((m, b) => Math.max(m, b.byMode[selectedMode] ?? 0), 0);
  const bars: PlaytimeBarModel[] = series.map((b) => {
    const ms = b.byMode[selectedMode] ?? 0;
    return {
      key: b.bucket,
      label: formatBucketLabel(b.bucket, bucket),
      title: `${formatBucketFull(b.bucket, bucket)} · ${formatDuration(ms)} ${modeLabel(selectedMode)}`,
      totalMs: ms,
      segments: ms > 0 ? [{ key: selectedMode, ms, colorVar, title: '' }] : [],
    };
  });
  return (
    <section className="analytics-section" data-testid="playtime-by-mode">
      <h2>Playtime by mode</h2>
      <p className="muted">Time played per {bucket} for a single mode. Bucketed in UTC.</p>
      <nav className="analytics-range-tabs analytics-mode-tabs" data-testid="playtime-mode-tabs">
        {GAME_MODES.map((mode) => (
          <button
            key={mode}
            type="button"
            className={mode === selectedMode ? 'active' : ''}
            onClick={() => setSelectedMode(mode)}
          >
            {modeLabel(mode)}
          </button>
        ))}
      </nav>
      <div className="analytics-dau-summary">
        <div className="analytics-dau-summary-item">
          <span className="analytics-dau-summary-value" style={{ color: colorVar }}>
            {formatDuration(selectedTotal)}
          </span>
          <span className="analytics-dau-summary-label">{modeLabel(selectedMode)} playtime</span>
        </div>
      </div>
      {selectedTotal === 0 ? (
        <p className="muted faint">No {modeLabel(selectedMode)} playtime recorded in this window.</p>
      ) : (
        <PlaytimeBarChart bars={bars} maxMs={maxMs} testId="playtime-mode-bars" />
      )}
    </section>
  );
}

function DauSection({ rows }: { rows: DashboardDauRow[] }) {
  if (rows.length === 0) {
    return null;
  }
  // A true "distinct players in this window" would need a server-side
  // aggregate. As proxies the summary surfaces peak DAU (the busiest
  // day) and sum-of-new (a real count, since each user's first-ever
  // event lands on exactly one day, so summing new-cohort entries
  // doesn't double-count anyone).
  const peakDau = Math.max(...rows.map((r) => r.dau));
  const totalNew = rows.reduce((sum, r) => sum + r.newUsers, 0);
  const max = Math.max(peakDau, 1);
  return (
    <section className="analytics-section">
      <h2>Daily active users</h2>
      <p className="muted">
        Distinct players with any tracked event that day. <strong>New</strong> = their
        first-ever event landed on that day; <strong>returning</strong> = anyone else
        active that day.
      </p>
      <div className="analytics-dau-summary">
        <div className="analytics-dau-summary-item">
          <span className="analytics-dau-summary-value">{peakDau.toLocaleString()}</span>
          <span className="analytics-dau-summary-label">Peak DAU</span>
        </div>
        <div className="analytics-dau-summary-item">
          <span className="analytics-dau-summary-value">{totalNew.toLocaleString()}</span>
          <span className="analytics-dau-summary-label">New users in window</span>
        </div>
        <div className="analytics-dau-legend">
          <span className="analytics-dau-swatch analytics-dau-swatch-return" />
          <span>Returning</span>
          <span className="analytics-dau-swatch analytics-dau-swatch-new" />
          <span>New</span>
        </div>
      </div>
      <div className="analytics-bars" data-testid="dau-bars">
        {rows.map((r) => {
          const returning = Math.max(0, r.dau - r.newUsers);
          const dauPct = (r.dau / max) * 100;
          return (
            <div
              className="analytics-bar-col"
              key={r.date}
              title={`${r.date}: ${r.dau} DAU (${returning} returning, ${r.newUsers} new)`}
            >
              <div className="analytics-bar-value">
                {r.dau > 0 ? r.dau.toLocaleString() : ''}
              </div>
              <div className="analytics-bar-track">
                <div className="analytics-bar-stack" style={{ height: `${dauPct}%` }}>
                  {r.newUsers > 0 && (
                    <div
                      className="analytics-bar-new"
                      style={{ flex: r.newUsers }}
                      title={`${r.newUsers} new`}
                    />
                  )}
                  {returning > 0 && (
                    <div
                      className="analytics-bar-returning"
                      style={{ flex: returning }}
                      title={`${returning} returning`}
                    />
                  )}
                </div>
              </div>
              <div className="analytics-bar-date">{r.date.slice(5)}</div>
              <div className="analytics-bar-new-sub">
                {r.newUsers > 0 ? `+${r.newUsers} new` : '\u00a0'}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function RetentionSection({ rows }: { rows: DashboardRetentionRow[] }) {
  const populated = rows.filter((r) => r.cohortSize > 0);
  if (populated.length === 0) {
    return null;
  }
  return (
    <section className="analytics-section">
      <h2>Retention cohorts</h2>
      <table className="analytics-table" data-testid="retention-table">
        <thead>
          <tr>
            <th>Cohort</th>
            <th>Size</th>
            <th>D1 retained</th>
            <th>D1 %</th>
            <th>D7 retained</th>
            <th>D7 %</th>
          </tr>
        </thead>
        <tbody>
          {populated.map((r) => (
            <tr key={r.cohortDate}>
              <td>{r.cohortDate}</td>
              <td>{r.cohortSize}</td>
              <td>{r.d1Retained}</td>
              <td>{(r.d1Rate * 100).toFixed(0)}%</td>
              <td>{r.d7Retained}</td>
              <td>{(r.d7Rate * 100).toFixed(0)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function RecentEventsSection({
  events,
  loading,
  songOnly,
  onToggleSongOnly,
}: {
  events: DashboardRecentEvent[] | null;
  loading: boolean;
  songOnly: boolean;
  onToggleSongOnly: (next: boolean) => void;
}) {
  return (
    <section className="analytics-section">
      <h2>Recent events</h2>
      <div className="analytics-recent-controls">
        <p className="muted">
          Last 50 events, newest first.{' '}
          {songOnly
            ? 'Showing song lifecycle only — toggle to include page views and session opens.'
            : 'Showing every event type — high-volume page views and session opens may push gameplay rows down.'}
        </p>
        <label className="analytics-toggle">
          <input
            type="checkbox"
            checked={songOnly}
            onChange={(e) => onToggleSongOnly(e.target.checked)}
            data-testid="recent-songs-only-toggle"
          />
          Songs only
        </label>
      </div>
      {loading && events === null && <p className="muted faint">Loading…</p>}
      {events && events.length === 0 && !loading && (
        <p className="muted faint">
          No events match the current filter.
          {songOnly && ' Try unchecking "Songs only".'}
        </p>
      )}
      {events && events.length > 0 && (
        <RecentEventsTable events={events} testId="recent-events" />
      )}
    </section>
  );
}

function RecentEventsTable({
  events,
  testId,
}: {
  events: DashboardRecentEvent[];
  testId: string;
}) {
  return (
    <div className="analytics-table-wrap">
      <table className="analytics-table analytics-table-compact" data-testid={testId}>
        <thead>
          <tr>
            <th>When</th>
            <th>Type</th>
            <th>Mode</th>
            <th title="For songs: the song slug. For page_view: the route the player navigated to.">
              Context
            </th>
            <th>Outcome</th>
            <th>Duration</th>
            <th>Player</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <RecentEventRow key={e.id} event={e} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecentEventRow({ event: e }: { event: DashboardRecentEvent }) {
  const context = recentEventContext(e);
  const playerLabel = playerDisplayLabel(e);
  return (
    <tr>
      <td className="analytics-when-cell" title={`${e.occurredAt} (UTC)`}>
        {formatLocalDateTime(e.occurredAt)}
      </td>
      <td>
        <EventTypeBadge type={e.eventType} />
      </td>
      <td>{e.mode ?? '—'}</td>
      <td
        className={context.mono ? 'mono analytics-context-cell' : 'analytics-context-cell'}
        title={context.text}
      >
        {context.text}
      </td>
      <td>
        {e.progressTier !== null ? progressTierLabel(e.progressTier as ProgressTier) : '—'}
      </td>
      <td>{e.durationMs !== null ? formatDuration(e.durationMs) : '—'}</td>
      <td className="analytics-player-cell" title={e.userUid ?? ''}>
        {playerLabel}
      </td>
    </tr>
  );
}

/**
 * Compose a friendly label for the "Player" column. Falls back through:
 *   displayName > email-local-part > "anon" > short uid > em-dash
 * so non-anonymous accounts show as their handle, signed-out anons
 * stay legible, and the operator can still click the row to see the
 * raw uid in the title attribute.
 *
 * Display names are trimmed before rendering so leading/trailing
 * whitespace doesn't get ellipsised inside the table cell.
 */
function playerDisplayLabel(e: DashboardRecentEvent): string {
  const trimmedName = e.displayName?.trim();
  if (trimmedName) {
    return trimmedName;
  }
  if (e.email) {
    const at = e.email.indexOf('@');
    return at > 0 ? e.email.slice(0, at) : e.email;
  }
  if (e.isAnonymous) {
    return 'anon';
  }
  if (e.userUid) {
    return e.userUid.slice(0, 10);
  }
  return '—';
}

/**
 * Format an ISO 8601 string as a readable local-time stamp like
 * `25 May, 20:15:32`. The same string is used for the dashboard's
 * "loaded at" indicator and every event row, so the operator sees
 * times in the timezone they're playing in — not UTC.
 *
 * The full ISO string is exposed via the cell's `title` attribute so
 * UTC is one hover away when needed.
 */
function formatLocalDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/**
 * Context text + monospace flag for the "Context" cell in the recent
 * events feed. Per event type:
 *  - song_*       → song slug (the existing mapSlug column)
 *  - page_view    → the route path from props.path (so the debug feed
 *                   actually tells you which page the user looked at)
 *  - session_open → no useful per-event context, render em-dash
 */
function recentEventContext(e: DashboardRecentEvent): { text: string; mono: boolean } {
  if (e.eventType === 'page_view') {
    const path = e.props && typeof e.props.path === 'string' ? e.props.path : null;
    if (path) {
      return { text: path, mono: true };
    }
  }
  if (e.mapSlug) {
    return { text: e.mapSlug, mono: false };
  }
  return { text: '—', mono: false };
}

function EventTypeBadge({ type }: { type: string }) {
  const variant = (() => {
    switch (type) {
      case 'song_start':
        return 'start';
      case 'song_end':
        return 'end';
      case 'song_abandon':
        return 'abandon';
      case 'page_view':
        return 'view';
      case 'session_open':
        return 'session';
      default:
        return 'other';
    }
  })();
  return <span className={`analytics-event-badge analytics-event-${variant}`}>{type}</span>;
}

/**
 * Format a duration for display. Used both for single-run durations
 * (avg run, recent-events feed) and for aggregate playtime totals
 * (per-mode / per-song sums), which can reach into hours. Shows the two
 * most significant units once past a minute — `Mm Ss`, then `Hh Mm`
 * past an hour where the trailing seconds are just noise.
 */
function formatDuration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const totalMinutes = Math.floor(seconds / 60);
  if (totalMinutes < 60) {
    return `${totalMinutes}m ${seconds % 60}s`;
  }
  const h = Math.floor(totalMinutes / 60);
  return `${h}h ${totalMinutes % 60}m`;
}

/**
 * Ultra-compact single-unit duration for cramped chart axis labels
 * (`9s`, `47m`, `1.4h`, `23h`). The precise multi-unit value lives in
 * {@link formatDuration} for tooltips and window summaries.
 */
function formatDurationShort(ms: number): string {
  const minutes = ms / 60_000;
  if (minutes < 1) {
    return `${Math.round(ms / 1000)}s`;
  }
  if (minutes < 60) {
    return `${Math.round(minutes)}m`;
  }
  const hours = minutes / 60;
  return hours >= 10 ? `${Math.round(hours)}h` : `${hours.toFixed(1)}h`;
}

/**
 * Short axis label for a playtime bucket. Hour buckets show `HH:00`,
 * day buckets show `Mon D` — both in UTC to match the server-side
 * bucketing (`date_trunc` runs in UTC).
 */
function formatBucketLabel(iso: string, unit: PlaytimeBucketUnit): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  if (unit === 'hour') {
    return `${String(d.getUTCHours()).padStart(2, '0')}:00`;
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** Verbose UTC label for a bucket, used in hover tooltips. */
function formatBucketFull(iso: string, unit: PlaytimeBucketUnit): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  if (unit === 'hour') {
    const label = d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'UTC',
    });
    return `${label} UTC`;
  }
  const label = d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
  return `${label} UTC`;
}

/** Multi-line hover text for a stacked playtime bar: window + total + per-mode split. */
function playtimeTooltip(b: DashboardPlaytimeBucket, unit: PlaytimeBucketUnit): string {
  const head = `${formatBucketFull(b.bucket, unit)} · ${formatDuration(b.totalMs)} total`;
  if (b.totalMs === 0) {
    return head;
  }
  const parts = GAME_MODES.filter((mode) => (b.byMode[mode] ?? 0) > 0).map(
    (mode) => `${modeLabel(mode)} ${formatDuration(b.byMode[mode] as number)}`,
  );
  return `${head}\n${parts.join(' · ')}`;
}

/**
 * Per-player drill-down: pick from the 100 most recently active
 * players and view their last 100 events. Loads the active-users list
 * lazily on mount and refetches both the list and the selected
 * player's events whenever the global Refresh button is hit.
 *
 * Owns its own loading + error state so a failed user lookup doesn't
 * tank the rest of the dashboard.
 *
 * Like the global feed, the events list defaults to song lifecycle
 * only — `page_view` is so high-volume it would otherwise drown the
 * window of 100 within a few minutes of normal browsing.
 */
function PlayerDrilldownSection({ refreshTick }: { refreshTick: number }) {
  const [users, setUsers] = useState<DashboardActiveUser[] | null>(null);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [events, setEvents] = useState<DashboardRecentEvent[] | null>(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [drilldownSongOnly, setDrilldownSongOnly] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setUsersError(null);
    fetchActiveUsers(100)
      .then((res) => {
        if (cancelled) {
          return;
        }
        setUsers(res.users);
        // Reconcile selection with the freshly-fetched list:
        //   - first load (selectedUid === null) → seed to top-of-list
        //   - refresh after the picked user dropped out of the top 100
        //     → snap to top so the dropdown shows a valid option
        // Reading `selectedUid` via the functional setter avoids putting
        // it in the effect deps (we explicitly DO NOT want to refetch
        // the active-users list every time the operator picks someone).
        setSelectedUid((current) => {
          if (current && res.users.some((u) => u.userUid === current)) {
            return current;
          }
          return res.users[0]?.userUid ?? null;
        });
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        const msg = err instanceof Error ? err.message : String(err);
        setUsersError(`Failed to load active users: ${msg}`);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshTick]);

  useEffect(() => {
    if (selectedUid === null) {
      setEvents(null);
      return;
    }
    let cancelled = false;
    setEventsLoading(true);
    setEventsError(null);
    const opts: RecentEventsOpts = drilldownSongOnly
      ? { limit: 100, userUid: selectedUid, eventTypes: SONG_EVENT_TYPES }
      : { limit: 100, userUid: selectedUid };
    fetchRecentEvents(opts)
      .then((res) => {
        if (cancelled) {
          return;
        }
        setEvents(res.events);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        const msg = err instanceof Error ? err.message : String(err);
        setEventsError(`Failed to load player events: ${msg}`);
      })
      .finally(() => {
        if (!cancelled) {
          setEventsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedUid, drilldownSongOnly, refreshTick]);

  return (
    <section className="analytics-section" data-testid="player-drilldown">
      <h2>Player drill-down</h2>
      <div className="analytics-recent-controls">
        <p className="muted">
          Pick from the 100 most recently active players to inspect their last 100 events. Sorted
          by most-recent activity (excluding replay-mode runs).
        </p>
        <label className="analytics-toggle">
          <input
            type="checkbox"
            checked={drilldownSongOnly}
            onChange={(e) => setDrilldownSongOnly(e.target.checked)}
            data-testid="drilldown-songs-only-toggle"
          />
          Songs only
        </label>
      </div>
      {usersError && (
        <p className="error-text" data-testid="drilldown-users-error">
          {usersError}
        </p>
      )}
      {users && users.length === 0 && (
        <p className="muted faint">No active players in the analytics table yet.</p>
      )}
      {users && users.length > 0 && (
        <div className="analytics-drilldown-controls">
          <label className="analytics-drilldown-label">
            <span className="muted">Player</span>
            <select
              className="analytics-drilldown-select"
              value={selectedUid ?? ''}
              onChange={(e) => setSelectedUid(e.target.value)}
              data-testid="drilldown-user-select"
            >
              {users.map((u) => (
                <option key={u.userUid} value={u.userUid}>
                  {playerDropdownLabel(u)}
                </option>
              ))}
            </select>
          </label>
          {selectedUid && <SelectedPlayerSummary user={users.find((u) => u.userUid === selectedUid) ?? null} />}
        </div>
      )}
      {eventsError && (
        <p className="error-text" data-testid="drilldown-events-error">
          {eventsError}
        </p>
      )}
      {eventsLoading && (
        <p className="muted faint">Loading events…</p>
      )}
      {events && events.length === 0 && !eventsLoading && (
        <p className="muted faint">
          No events for this player{drilldownSongOnly ? ' under the current filter' : ''}.
          {drilldownSongOnly && ' Try unchecking "Songs only".'}
        </p>
      )}
      {events && events.length > 0 && (
        <RecentEventsTable events={events} testId="drilldown-events" />
      )}
    </section>
  );
}

function playerDropdownLabel(u: DashboardActiveUser): string {
  const trimmedName = u.displayName?.trim();
  const handle = trimmedName
    ? trimmedName
    : u.email
      ? u.email
      : u.isAnonymous
        ? 'anon'
        : `${u.userUid.slice(0, 10)}…`;
  const lastSeen = formatLocalDateTime(u.lastSeenAt);
  const flag = u.hasPlayed ? '' : ' (browsed only)';
  return `${handle} — ${lastSeen}${flag} (${u.eventCount.toLocaleString()} events)`;
}

function SelectedPlayerSummary({ user }: { user: DashboardActiveUser | null }) {
  if (!user) {
    return null;
  }
  return (
    <div className="analytics-drilldown-summary">
      <div className="analytics-drilldown-summary-row">
        <span className="muted">UID</span>
        <code className="mono">{user.userUid}</code>
        <CopyButton value={user.userUid} />
      </div>
      {user.email && (
        <div className="analytics-drilldown-summary-row">
          <span className="muted">Email</span>
          <code className="mono">{user.email}</code>
        </div>
      )}
      <div className="analytics-drilldown-summary-row">
        <span className="muted">Last seen</span>
        <span>{formatLocalDateTime(user.lastSeenAt)}</span>
      </div>
    </div>
  );
}

